import { Store } from 'vuex';

import { SelectableMenuItem, ItemCardCustomization, ItemCardCustomizationType, ItemCardSelectionInfoKind, VariantMenuItem, MenuItem } from '@/models';

import { AppState } from '@/store';
import { MUTATIONS as MEAL_BUILDER_MUTATIONS } from '@/store/meal-builder';
import { Logger } from '@/services/logger/logger';

import { ItemSelector, QuantityItemSelector, SingleItemSelector } from '.';
import { MealBuilderAnalyticService } from '@/services/meal-builder/meal-builder-analytics.service';
import uniq from 'lodash/uniq';
import { AnalyticsService } from '@/services/analytics/analytics.service';

export interface AggregateItemSelector extends ItemSelector<any> {
  getAllSelectedItems(): SelectableMenuItem[];
  getSelectedItems(items: SelectableMenuItem[]): SelectableMenuItem[];
  deselectAllItems(items: SelectableMenuItem[], shallowDeselect?: boolean): void;
  resetItemSelections(): void;
  selectItemShallow(item: SelectableMenuItem, args?: any): boolean;
  synchronizeItems(items: SelectableMenuItem[]): void;
}

export class AppAggregateItemSelector implements AggregateItemSelector {
  constructor(
    protected store: Store<AppState>,
    protected quantityItemSelector: QuantityItemSelector,
    protected singleItemSelector: SingleItemSelector,
    protected logger: Logger,
    protected mealBuilderAnalytics: MealBuilderAnalyticService,
    protected analytics: AnalyticsService,
  ) {}

  getSelectedItems(items: SelectableMenuItem[]) {
    // TODO-ked6gq: improve perf
    return items.filter(i => this.isItemSelected(i));
  }

  selectItem(item: SelectableMenuItem, args: any) {
    const didSelect = this.selectItemShallow(item, args);

    this.synchronizeItems([item]);

    return didSelect;
  }

  selectItemShallow(item: SelectableMenuItem, args: any) {
    const selector = this.getItemSelectorForSelectionKind(item.selectionInfo.kind);
    const didSelect = selector.selectItem(item, args);

    return didSelect;
  }

  deselectItem(item: SelectableMenuItem) {
    const toSynchronize = this.deselectItemShallow(item);

    this.synchronizeItems(toSynchronize);
  }

  deselectAllItems(items: SelectableMenuItem[], shallowDeselect?: boolean) {
    const toSynchronize = items.reduce((acc, item) => {
      for (const i of this.deselectItemShallow(item)) {
        acc.push(i);
      }

      return acc;
    }, [] as SelectableMenuItem[]);

    if (!shallowDeselect) {
      this.synchronizeItems(toSynchronize);
    }
  }

  isItemSelected(item: SelectableMenuItem) {
    const selector = this.getItemSelectorForSelectionKind(item.selectionInfo.kind);
    return selector.isItemSelected(item);
  }

  isDeselecting(item: SelectableMenuItem, args: any) {
    const selector = this.getItemSelectorForSelectionKind(item.selectionInfo.kind);
    return selector.isDeselecting(item, args);
  }

  getQuantityAfterSelection(item: SelectableMenuItem, args: any) {
    const selector = this.getItemSelectorForSelectionKind(item.selectionInfo.kind);
    return selector.getQuantityAfterSelection(item, args);
  }

  getItemQuantity(item: SelectableMenuItem) {
    const selector = this.getItemSelectorForSelectionKind(item.selectionInfo.kind);
    return selector.getItemQuantity(item);
  }

  getEffectiveItemQuantity(item: SelectableMenuItem) {
    if (item.groupedItemSelectionOptions) {
      const count = item.groupedItemSelectionOptions.find(i => i.id === item.item.itemId)?.countTowardsContentMax;
      if (count) {
        return count;
      }
    }

    const customization = item.customizations.find(c => c.selected);
      if (customization?.type == ItemCardCustomizationType.Customization && customization.customization.countTowardsContentMax != 0) {
      return 1 + customization.customization.countTowardsContentMax;
    }

    const selector = this.getItemSelectorForSelectionKind(item.selectionInfo.kind);
    return selector.getEffectiveItemQuantity(item);
  }

  getMaxQuantity(item: SelectableMenuItem) {
    const selector = this.getItemSelectorForSelectionKind(item.selectionInfo.kind);
    return selector.getMaxQuantity(item);
  }

  copySelectionTo(target: SelectableMenuItem, src: SelectableMenuItem, shallowSelection: boolean) {
    if (target.selectionInfo.kind != src.selectionInfo.kind) {
      this.logger.unreachable('Target item does not share a selection kind with src', { target, src });
      return false;
    }

    const selector = this.getItemSelectorForSelectionKind(target.selectionInfo.kind);
    selector.copySelectionTo(target, src, shallowSelection);

    if (!shallowSelection) {
      this.synchronizeItems([target]);
    }

    return true;
  }

  /**
   * Synchronizes items that may have been selected / deselected without notifying the app
   */
  synchronizeItems(items: SelectableMenuItem[]) {
    const [toAdd, toRemove] = items.reduce(
      (acc, item) => {
        if (this.isItemSelected(item)) {
          acc[0].push(item);
        } else {
          acc[1].push(item);
        }

        return acc;
      },
      [[], []] as SelectableMenuItem[][],
    );

    this.setSelectedItems(this.calculateSelectedItemsAfterOperations(toAdd, toRemove));
  }

  resetItemSelections() {
    this.deselectAllItems(this.getAllSelectedItems());
  }

  getAllSelectedItems(): SelectableMenuItem[] {
    return this.store.state.mealBuilder.selectedItems;
  }

  protected getItemSelectorForSelectionKind(kind: ItemCardSelectionInfoKind): ItemSelector<any> {
    switch (kind) {
      case ItemCardSelectionInfoKind.Quantity:
        return this.quantityItemSelector;
      case ItemCardSelectionInfoKind.Single:
        return this.singleItemSelector;
      default:
        throw new Error(`Unknown item card selection kind: ${kind}`);
    }
  }

  protected setSelectedItems(items: SelectableMenuItem[]): void {
    this.store.commit(MEAL_BUILDER_MUTATIONS.SET_SELECTED_ITEMS, items);
  }

  protected addToSelectedItems(...items: SelectableMenuItem[]) {
    this.setSelectedItems(this.calculateSelectedItemsAfterOperations(items, []));
  }

  // TODO: add unit testing and attempt to make this smaller
  protected calculateSelectedItemsAfterOperations(toAdd: SelectableMenuItem[], toRemove: SelectableMenuItem[]): SelectableMenuItem[] {
    const selectedItems = this.getAllSelectedItems();

    // Add items to selectedItems
    const afterAdd = selectedItems.concat(toAdd.filter(item => !selectedItems.find(i => i == item)));

    // Remove items from intermediate result
    const afterRemove = [...afterAdd];
    for (const item of toRemove) {
      const itemIndex = afterRemove.indexOf(item);
      if (itemIndex == -1) {
        this.logger.debug('Failed to find item in selectedItems -- this may be an error', { item, selectedItems });
        continue;
      }

      afterRemove.splice(itemIndex, 1);
    }

    // Handle grouped menu items like cauli rice
    const selectedNonDefaultGroupedItems = uniq(selectedItems.filter(i => i.groupedItemSelectionOptions && i.defaultItemId !== i.item.itemId));
    if (selectedNonDefaultGroupedItems.length) {
      return afterRemove.map(i => {
        if (((i as unknown) as VariantMenuItem).contents) {
          // Replace any found grouped item with its proper version for the variant migration
          const updatedContents = (((i as unknown) as VariantMenuItem).contents?.map(contentItem => {
            const selectedGroupedItem = selectedNonDefaultGroupedItems.find(groupedItem => groupedItem.defaultItemId === ((contentItem as unknown) as SelectableMenuItem).item.itemId);
            return selectedGroupedItem ?? contentItem;
          }) as unknown) as SelectableMenuItem<MenuItem, MenuItem>;
          // Update variant's contents array to be a new one containing any updated menu items
          ((((i as unknown) as VariantMenuItem).contents as unknown) as SelectableMenuItem<MenuItem, MenuItem>) = updatedContents;
          return i;
        } else {
          return i;
        }
      });
    }
    
    return afterRemove;
  }

  protected removeFromSelectedItems(...items: SelectableMenuItem[]) {
    this.setSelectedItems(this.calculateSelectedItemsAfterOperations([], items));
  }

  protected deselectCustomization(customization: ItemCardCustomization, shallowDeselect: boolean): SelectableMenuItem[] | void {
    customization.selected = false;

    if (customization.type == ItemCardCustomizationType.SelectableItem && this.isItemSelected(customization.item)) {
      return shallowDeselect ? this.deselectItemShallow(customization.item) : this.deselectItem(customization.item);
    }
  }

  /**
   * Deselects all customizations for an item
   * @returns Any items that need to be synchronized as the result of a shallow deselect
   */
  protected deselectAllItemCustomizations(item: SelectableMenuItem, shallowDeselect: boolean): SelectableMenuItem[] {
    const itemsToSynchronize: SelectableMenuItem[] = [];
    for (const customization of item.customizations) {
      const maybeItemsToSynch = this.deselectCustomization(customization, shallowDeselect);

      if (shallowDeselect && !!maybeItemsToSynch) {
        itemsToSynchronize.splice(itemsToSynchronize.length - 1, 0, ...maybeItemsToSynch);
      }
    }

    return itemsToSynchronize;
  }

  /**
   * Deselects an item
   * @returns Any items that need to be synchronized as the result of a shallow deselect
   */
  protected deselectItemShallow(item: SelectableMenuItem): SelectableMenuItem[] {
    const selector = this.getItemSelectorForSelectionKind(item.selectionInfo.kind);
    selector.deselectItem(item);

    return [item].concat(this.deselectAllItemCustomizations(item, true));
  }
}
